import type { FC, ReactNode } from 'react'
import type { GalleryItem } from '@/types'

import Image from 'next/image'
import { Expand } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { Lightbox } from '@/components/landing/lightbox'
import { galleryCategories, galleryCollection } from '@/constants/experience'

const RATIOS: Record<GalleryItem['shape'], string> = {
    portrait: 'aspect-[3/4]',
    square: 'aspect-square',
    landscape: 'aspect-[3/2]',
    wide: 'aspect-[16/9]'
}

/** The full photo library as a filterable masonry wall with a full-screen viewer. */
export const GalleryGrid: FC = (): ReactNode => {
    const [category, setCategory] = useState('All')
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    const items = useMemo(() => {
        return category === 'All' ? galleryCollection : galleryCollection.filter((item) => item.category === category)
    }, [category])

    const close = useCallback((): void => setOpenIndex(null), [])
    const navigate = useCallback((index: number): void => setOpenIndex(index), [])

    return (
        <section
            id='photographs'
            aria-labelledby='photographs-heading'
            className='relative z-10 -mt-px rounded-t-[1.75rem] bg-bone text-charcoal shadow-[0_-30px_70px_-20px_rgba(9,17,12,0.55)] sm:rounded-t-[2.5rem]'
        >
            <div className='px-5 pb-24 pt-20 sm:px-8 lg:px-12 lg:pb-32 lg:pt-28'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <div>
                        <h2 id='photographs-heading' className='flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-charcoal/70'>
                            <span className='tabular-nums'>
                                01
                            </span>

                            <span aria-hidden='true' className='h-px w-8 bg-current opacity-40' />

                            <span>
                                The photographs
                            </span>
                        </h2>

                        <p className='mt-6 max-w-xl text-[clamp(1.5rem,2.8vw,2.6rem)] font-light leading-[1.15] tracking-[-0.02em]'>
                            {items.length} frames from across the estate. <em className='font-serif font-normal'>Select any one</em> to see it full size.
                        </p>
                    </div>

                    <div role='group' aria-label='Filter photographs' className='flex flex-wrap gap-2'>
                        {galleryCategories.map((name) => (
                            <button
                                key={name}
                                type='button'
                                onClick={() => setCategory(name)}
                                aria-pressed={category === name}
                                className={cn(
                                    'rounded-full border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors',
                                    category === name
                                        ? 'border-forest bg-forest text-bone'
                                        : 'border-charcoal/20 text-charcoal/75 hover:border-charcoal/60 hover:text-charcoal'
                                )}
                            >
                                {name}
                            </button>
                        ))}
                    </div>
                </div>

                <ul key={category} className='mt-12 columns-1 gap-5 animate-in fade-in duration-700 sm:columns-2 lg:mt-16 lg:columns-3 lg:gap-6'>
                    {items.map((item, itemIndex) => (
                        <li key={item.src} className='mb-5 break-inside-avoid lg:mb-6'>
                            <button
                                type='button'
                                onClick={() => setOpenIndex(itemIndex)}
                                aria-label={`View photograph: ${item.caption}`}
                                className={cn('group relative block w-full overflow-hidden rounded-[4px] bg-sand', RATIOS[item.shape])}
                            >
                                <Image
                                    src={item.src}
                                    alt={item.alt}
                                    fill
                                    sizes='(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
                                    className='object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]'
                                    style={{ objectPosition: item.position }}
                                />

                                <span aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-pine/75 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 group-focus-visible:opacity-100' />

                                <span
                                    aria-hidden='true'
                                    className='absolute inset-x-0 bottom-0 flex translate-y-3 items-end justify-between gap-4 p-4 text-left text-bone opacity-0 transition-[opacity,translate] duration-500 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100 sm:p-5'
                                >
                                    <span className='font-serif text-lg italic leading-tight sm:text-xl'>
                                        {item.caption}
                                    </span>

                                    <span className='flex shrink-0 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]'>
                                        {pad(itemIndex + 1)}
                                        <Expand className='size-4' />
                                    </span>
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            <Lightbox
                items={items}
                index={openIndex}
                onClose={close}
                onNavigate={navigate}
            />
        </section>
    )
}
